/**
 * Request logging and metrics tracking
 */

export interface RequestMetadata {
	requestId: string;
	provider: string;
	model: string;
	timestamp: number;
	metadata?: Record<string, any>;
}

export interface RequestTracker extends RequestMetadata {
	startTime: number;
}

export interface CompletedRequest extends RequestMetadata {
	latency: number;
	success: boolean;
	tokens?: number;
	error?: string;
}

export interface ProviderStats {
	requests: number;
	avgLatency: number;
	tokens: number;
	errors: number;
}

export interface LoggerStats {
	totalRequests: number;
	successfulRequests: number;
	failedRequests: number;
	averageLatency: number;
	totalTokensUsed: number;
	providerBreakdown: Record<string, ProviderStats>;
}

/**
 * Simple request logger for tracking metrics
 */
export class RequestLogger {
	private requests: CompletedRequest[] = [];
	private enabled: boolean;

	constructor(enabled: boolean = false) {
		this.enabled = enabled;
	}

	/**
	 * Start tracking a request
	 */
	startRequest(
		provider: string,
		model: string,
		metadata?: Record<string, any>
	): RequestTracker {
		return {
			requestId: this.generateId(),
			provider,
			model,
			timestamp: Date.now(),
			startTime: Date.now(),
			metadata,
		};
	}

	/**
	 * Complete and log a request
	 */
	logRequest(tracker: RequestTracker, success: boolean, tokens?: number, error?: string): void {
		if (!this.enabled) return;

		const latency = Date.now() - tracker.startTime;

		this.requests.push({
			requestId: tracker.requestId,
			provider: tracker.provider,
			model: tracker.model,
			timestamp: tracker.timestamp,
			metadata: tracker.metadata,
			latency,
			success,
			tokens,
			error,
		});
	}

	/**
	 * Get aggregated statistics
	 */
	getStats(): LoggerStats {
		const successful = this.requests.filter((r) => r.success);
		const failed = this.requests.filter((r) => !r.success);

		const totalLatency = this.requests.reduce((sum, r) => sum + r.latency, 0);
		const totalTokens = this.requests.reduce((sum, r) => sum + (r.tokens || 0), 0);

		// Provider breakdown
		const providerBreakdown: Record<string, ProviderStats> = {};

		for (const request of this.requests) {
			if (!providerBreakdown[request.provider]) {
				providerBreakdown[request.provider] = {
					requests: 0,
					avgLatency: 0,
					tokens: 0,
					errors: 0,
				};
			}

			const stats = providerBreakdown[request.provider];
			stats.requests++;
			stats.tokens += request.tokens || 0;
			if (!request.success) stats.errors++;
		}

		// Calculate average latencies per provider
		for (const provider in providerBreakdown) {
			const providerRequests = this.requests.filter((r) => r.provider === provider);
			const providerLatency = providerRequests.reduce((sum, r) => sum + r.latency, 0);
			providerBreakdown[provider].avgLatency =
				providerRequests.length > 0 ? providerLatency / providerRequests.length : 0;
		}

		return {
			totalRequests: this.requests.length,
			successfulRequests: successful.length,
			failedRequests: failed.length,
			averageLatency: this.requests.length > 0 ? totalLatency / this.requests.length : 0,
			totalTokensUsed: totalTokens,
			providerBreakdown,
		};
	}

	/**
	 * Get all requests (for detailed analysis)
	 */
	getRequests(): CompletedRequest[] {
		return [...this.requests];
	}

	/**
	 * Clear all logged requests
	 */
	clear(): void {
		this.requests = [];
	}

	/**
	 * Enable or disable logging
	 */
	setEnabled(enabled: boolean): void {
		this.enabled = enabled;
	}

	private generateId(): string {
		return `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
	}
}

/**
 * Global logger instance
 */
let globalLogger: RequestLogger | null = null;

/**
 * Get or create global logger
 */
export function getRequestLogger(enabled: boolean = false): RequestLogger {
	if (!globalLogger) {
		globalLogger = new RequestLogger(enabled);
	} else {
		globalLogger.setEnabled(enabled);
	}
	return globalLogger;
}

